import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import Storage from "@aws-amplify/storage";
import { API, graphqlOperation } from "aws-amplify";
import { parseFile, getRestaurant } from "../graphql/queries";

import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";
import arrayMove from "array-move";

// Components
import {
  Container,
  Table,
  Row,
  Col,
  Button,
  Spinner,
  Modal,
  Alert,
  ButtonToolbar,
  ButtonGroup,
  FormLabel,
  Card,
} from "react-bootstrap";

// Actions
import {
  TOGGLE_CREATE_MENU,
  TOGGLE_UPDATE_MENU,
  DELETE_MENU_SUCCESS,
  UPDATE_RESTAURANT_SUCCESS,
} from "../actions/ActionTypes";
import {
  deleteMenuItem,
  getMenus,
  parseFileToJson,
  handleUpdateRestaurant,
} from "../actions/Menu";

// Helpers
import {
  formatMenusGeneralSetting,
  getMenuFileFromUrl,
  shouldWriteMenuJson,
} from "../helpers/menu";
import { IMAGES } from "../constants";
import RippleButton from "./Onboarding/RippleButton";

export const ManageMenus = ({
  restaurant,
  dispatch,
  getMenus,
  menus,
  history,
}) => {
  const getMenusCallback = useCallback(getMenus, [getMenus]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sortMenus, setSortMenus] = useState([]);
  const [deleteMenuState, setDeleteMenuState] = useState({
    toggleModal: false,
    menuId: null,
    isProcessing: false,
    error: "",
    progressMessage: "",
  });

  const [publishMenuState, setPublishMenuState] = useState({
    isProcessing: false,
    error: "",
    previewUrl: "",
  });

  useEffect(() => {
    const loadMenus = async (restaurantId) => {
      try {
        setLoading(true);
        await getMenusCallback(restaurantId);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    loadMenus(restaurant.id);
  }, [restaurant.id, getMenusCallback]);

  useEffect(() => {
    setSortMenus(menus);
  }, [menus]);

  const handleAddMenu = () => {
    dispatch({ type: TOGGLE_CREATE_MENU });
  };

  const handleEditMenu = (menuId) => {
    dispatch({ type: TOGGLE_UPDATE_MENU, data: menuId });
  };

  const handleDeleteMenu = async () => {
    /**
     * Do update the generalInfo.json after delete menu
     */
    const { menuId } = deleteMenuState;
    let logoUrl = null;
    let backgroundImageUrl = null;

    try {
      setDeleteMenuState((prevState) => ({
        ...prevState,
        isProcessing: true,
        progressMessage: "Deleting your menu...",
      }));

      // Request delete menu by id. Then get the latest data after delete
      const { updatedRestaurant } = await deleteMenuItem(menuId);

      // Dispatch to store after delete success
      dispatch({
        type: DELETE_MENU_SUCCESS,
        payload: menuId,
      });

      /**
       * IMPORTANT NOTE
       * Do update the generalInfo.json after delete menu
       */
      const updatedMenus = updatedRestaurant.menus.items;

      // Get the right "folder" if have migration
      let folderId = updatedRestaurant.migratedMenuId
        ? updatedRestaurant.migratedMenuId
        : updatedRestaurant.id;
      const folderName = `${folderId}-prod`;

      setDeleteMenuState((prevState) => ({
        ...prevState,
        progressMessage: "Menu has been Deleted! Publishing your menu(s)...",
      }));

      // Update generalInfo.json
      const {
        id,
        logoKey,
        displayLogo,
        displayDate,
        name,
        introduction,
        backgroundImageKey,
        displayBackground,
        dateText,
        descriptionText,
        restaurantName,
        displayCurrencySymbol,
        bookmarksEnabled,
        loadSectionOnly,
        zoomDisabled,
      } = updatedRestaurant;

      // Get the menusPosition fallback to empty array
      const menusPosition = updatedRestaurant.menusPosition || [];

      // If there's logo and setting is on
      if (displayLogo && logoKey) {
        logoUrl = "./logo.png";
      }

      // If there's background image and setting is on
      if (displayBackground && backgroundImageKey) {
        backgroundImageUrl = "./backgroundImage.png";
      }

      // Upload general setting config
      const generalInfo = {
        // Restaurant setting
        id,
        name,
        introduction,
        displayDate,
        logoUrl,
        publishedDate: new Date(),
        backgroundImageUrl,
        displayBackground,
        displayCurrencySymbol,
        dateText,
        descriptionText,
        restaurantName,
        menusPosition,
        bookmarksEnabled,
        loadSectionOnly,
        zoomDisabled,
        // Menus setting
        menus: formatMenusGeneralSetting(updatedMenus, null),
      };

      // Upload update generalInfo.json
      await Storage.put(
        folderName + "/generalInfo.json",
        JSON.stringify(generalInfo),
        {
          contentType: "application/json",
        }
      );

      await API.graphql(
        graphqlOperation(parseFile, {
          folderName,
          generalKey: `public/${folderName}/generalInfo.json`,
        }),
        []
      );

      // Close confirm delete modal and reset state
      handleCloseModal();
    } catch (err) {
      const error =
        err?.errors && err.errors[0].message
          ? err.errors[0].message
          : err.message;

      setDeleteMenuState((prevState) => ({
        ...prevState,
        isProcessing: false,
        error,
      }));
    }
  };

  const toggleModalConfirmDelete = (menuId) => {
    setDeleteMenuState((prevState) => ({
      ...prevState,
      toggleModal: true,
      menuId,
    }));
  };

  const handleCloseModal = () => {
    // Reset delete state
    setDeleteMenuState({
      toggleModal: false,
      isProcessing: false,
      menuId: "",
      error: "",
      progressMessage: "",
    });
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setSortMenus((prevState) => arrayMove(prevState, oldIndex, newIndex));
  };

  const handlePublish = async (isPreview) => {
    let logoUrl = null;
    let backgroundImageUrl = null;
    const menusPublishUpdate = sortMenus.map((menu) => menu.id);

    try {
      setPublishMenuState((prevState) => ({
        ...prevState,
        error: "",
        isProcessing: true,
      }));

      // Get the latest restaurant data
      const { data } = await API.graphql(
        graphqlOperation(getRestaurant, { id: restaurant.id })
      );

      if (data && data.getRestaurant) {
        const {
          id,
          logoKey,
          displayLogo,
          displayDate,
          name,
          introduction,
          backgroundImageKey,
          displayBackground,
          dateText,
          descriptionText,
          restaurantName,
          displayCurrencySymbol,
          migratedMenuId,
          bookmarksEnabled,
          loadSectionOnly,
          zoomDisabled,
          menus,
        } = data.getRestaurant;

        // Latest menus from latest restaurant data
        const updatedMenus = menus.items;

        // Get the right folder
        let folderId = migratedMenuId ? migratedMenuId : id;
        const folderName = `${folderId}-${isPreview ? "pre" : "prod"}`;

        // If there's logo and setting is on
        if (displayLogo && logoKey) {
          logoUrl = "./logo.png";
        }

        // If there's background image and setting is on
        if (displayBackground && backgroundImageKey) {
          backgroundImageUrl = "./backgroundImage.png";
        }

        // Upload general setting config
        const generalInfo = {
          // Restaurant setting
          id,
          name,
          introduction,
          displayDate,
          logoUrl,
          publishedDate: new Date(),
          backgroundImageUrl,
          displayBackground,
          displayCurrencySymbol,
          dateText,
          descriptionText,
          restaurantName,
          bookmarksEnabled,
          zoomDisabled,
          loadSectionOnly,
          menusPosition: menusPublishUpdate,

          // Menus setting
          menus: formatMenusGeneralSetting(updatedMenus, null),
        };

        // Upload update generalInfo.json
        await Storage.put(
          folderName + "/generalInfo.json",
          JSON.stringify(generalInfo),
          {
            contentType: "application/json",
          }
        );

        let menuItem = null;
        let jsonKeys = [];
        for (menuItem of updatedMenus) {
          const fileLocationSplit = menuItem.fileLocation.split("/");
          let fileName =
            fileLocationSplit.length > 1 ? fileLocationSplit[1] : "";
          let file = await getMenuFileFromUrl(
            await Storage.get(menuItem.fileLocation),
            fileName
          );
          // Parse the file to json object
          let jsonContent = await parseFileToJson(file);

          // Integrated menus: keep the menu json the integration wrote
          if (
            await shouldWriteMenuJson(
              jsonContent,
              `${folderName}/menu-${menuItem.id}.json`
            )
          ) {
            await Storage.put(
              `${folderName}/menu-${menuItem.id}.json`,
              JSON.stringify(jsonContent),
              {
                contentType: "application/json",
              }
            );
          }

          jsonKeys.push(`public/${folderName}/menu-${menuItem.id}.json`);
        }

        const parseFileData = await API.graphql(
          graphqlOperation(parseFile, {
            folderName,
            generalKey: `public/${folderName}/generalInfo.json`,
            jsonKey: jsonKeys,
            logoKey: logoKey ? `public/${logoKey}` : null,
            backgroundImageKey: backgroundImageKey
              ? `public/${backgroundImageKey}`
              : null,
          }),
          []
        );

        /**
         * Update the "menusPosition" of restaurant after preview or publish
         */
        let restaurantInput = {
          id,
          menusPosition: menusPublishUpdate,
        };
        if (parseFileData.data.parseFile !== "error") {
          restaurantInput.restaurantUrl = parseFileData.data.parseFile;
        }
        const updatedRestaurant = await handleUpdateRestaurant(restaurantInput);

        setPublishMenuState({
          error: "",
          isProcessing: false,
          previewUrl: isPreview ? parseFileData.data.parseFile : "",
        });

        dispatch({
          type: UPDATE_RESTAURANT_SUCCESS,
          payload: updatedRestaurant,
        });

        // If user select "publish" menu, force them to /qr-code.
        if (!isPreview) {
          history.push("/qr-code");
        }

        return;
      }

      throw new Error("Somethings went wrong! Please try again!");
    } catch (e) {
      setPublishMenuState({
        previewUrl: "",
        error: "Somethings went wrong! Please try again!",
        isProcessing: false,
      });
    }
  };

  return (
    <>
      <Modal
        animation
        show={deleteMenuState.toggleModal}
        onHide={handleCloseModal}
        dialogClassName="setting-modal"
      >
        <Modal.Header>
          <Modal.Title>Delete Menu</Modal.Title>
          <img
            src={IMAGES.CLOSE_ICON}
            alt="close-icon"
            width="44"
            height="44"
            onClick={handleCloseModal}
          />
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure to delete this menu?</p>
          {deleteMenuState.progressMessage && (
            <p className="font-italic text-muted d-flex align-items-center">
              {deleteMenuState.progressMessage}
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="isProcessing"
                className="ml-1"
                aria-hidden="true"
              />
            </p>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center">
          <Button
            variant="outline-dark"
            onClick={handleCloseModal}
            className="fileInput-label setting-button bg-white text-dark"
          >
            Cancel
          </Button>
          <Button
            variant="dark"
            onClick={handleDeleteMenu}
            disabled={deleteMenuState.isProcessing}
            data-testid="delete-menu"
            className="fileInput-label setting-button"
          >
            {deleteMenuState.isProcessing ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="isProcessing"
                className="ml-1"
                aria-hidden="true"
              />
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Container fluid className="wrapper px-0">
        <Row className="justify-content-between align-between mx-0 mb-4 title-setting-wrapper">
          <div className="d-flex">
            <h3 className="title-setting title-menu-setting">Manage Menus</h3>
            {loading && (
              <Spinner
                as="span"
                animation="border"
                role="status"
                aria-hidden="true"
                className="ml-1"
              />
            )}
          </div>
          <Button
            variant="dark"
            size="sm"
            onClick={handleAddMenu}
            disabled={loading}
            data-testid="add-menu"
            className="download-template add-menu-button"
          >
            Add Menu
            <img src={IMAGES.PLUS_ICON} alt="plus" height={17} width={17} />
          </Button>
        </Row>
        {error && (
          <Row className="mx-0 px-md-4 px-0">
            <Alert variant="danger" className="w-100">
              {error}
            </Alert>
          </Row>
        )}
        <Row className="mx-0 px-md-4 px-0">
          <Col>
            <MenuTable
              menus={sortMenus}
              handleEditMenu={handleEditMenu}
              toggleModalConfirmDelete={toggleModalConfirmDelete}
              onSortEnd={onSortEnd}
              useDragHandle
              lockAxis="y"
              helperClass="drag-wrapper"
            />
          </Col>
        </Row>
        <hr />
        {publishMenuState.error && (
          <Alert variant="danger">{publishMenuState.error}</Alert>
        )}
        {publishMenuState.previewUrl && (
          <div>
            <Row className="mt-4 mb-2">
              <Col>
                <FormLabel className="text-muted font-italic">
                  Preview was generated, use this link to view:
                </FormLabel>
              </Col>
            </Row>
            <Row>
              <Col>
                <Card body>
                  <a
                    href={publishMenuState.previewUrl}
                    className="font-weight-bold maroon"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {publishMenuState.previewUrl}
                  </a>
                </Card>
              </Col>
            </Row>
          </div>
        )}

        {sortMenus.length ? (
          <Row className="mt-3 mb-2">
            <Col>
              <ButtonToolbar
                aria-label="Toolbar with button groups"
                className="justify-content-center"
              >
                <ButtonGroup className="mr-4" aria-label="Third group">
                  <Button
                    variant="dark"
                    className="fileInput-label setting-button"
                    disabled={publishMenuState.isProcessing}
                    onClick={() => {
                      handlePublish(true);
                    }}
                  >
                    Preview
                  </Button>
                </ButtonGroup>
                <ButtonGroup aria-label="Fourth group">
                  <RippleButton
                    customStyles="setting-button"
                    type
                    onClick={() => {
                      handlePublish(false);
                    }}
                    disabled={publishMenuState.isProcessing}
                  >
                    Publish
                    {publishMenuState.isProcessing && (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        className="ml-1"
                        aria-hidden="true"
                      />
                    )}
                  </RippleButton>
                </ButtonGroup>
              </ButtonToolbar>
            </Col>
          </Row>
        ) : null}
      </Container>
    </>
  );
};

const DragHandle = SortableHandle(() => (
  <span className="drag-controller" style={{ pointerEvent: "pointer" }}></span>
));

export const MenuItem = SortableElement((props) => {
  const { menu, handleEditMenu, toggleModalConfirmDelete } = props;

  const onEditMenu = () => {
    handleEditMenu(menu.id);
  };

  const onDeleteMenu = () => {
    toggleModalConfirmDelete(menu.id);
  };

  return (
    <>
      {menu ? (
        <tr>
          <td style={{ width: 31, padding: 0 }}>
            <DragHandle />
          </td>
          <td>
            <span className="menu-name text-truncate">{menu.name}</span>
          </td>
          <td align="right" className="pr-0">
            <div>
              <Button
                size="sm"
                onClick={onEditMenu}
                className="menu-button bg-white"
              >
                Edit
              </Button>
              <Button
                size="sm"
                onClick={onDeleteMenu}
                className="menu-button border-0"
              >
                Delete
              </Button>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
});

export const MenuTable = SortableContainer((props) => {
  const { menus, handleEditMenu, toggleModalConfirmDelete } = props;

  return (
    <Table hover className="mt-4 menu-table">
      <thead>
        <tr>
          <th></th>
          <th>Menu Name</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {menus.length ? (
          menus.map((menu, index) => (
            <MenuItem
              menu={menu}
              key={menu?.id}
              index={index}
              handleEditMenu={handleEditMenu}
              toggleModalConfirmDelete={toggleModalConfirmDelete}
            />
          ))
        ) : (
          <tr>
            <td />
            <td>
              Nothing here, please click on <strong>Add Menu</strong> to begin
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  );
});

MenuItem.propTypes = {
  menu: PropTypes.object.isRequired,
  handleEditMenu: PropTypes.func.isRequired,
  toggleModalConfirmDelete: PropTypes.func.isRequired,
};

ManageMenus.propTypes = {
  restaurant: PropTypes.object.isRequired,
  getMenus: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  menus: PropTypes.array.isRequired,
  history: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  restaurant: state.restaurant.restaurant,
  auth: state.auth,
  menus: state.menu.menus,
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  ...bindActionCreators({ getMenus }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ManageMenus);
